import { useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Euro,
  Eye,
  KeyRound,
  LogOut,
  Menu,
  Minus,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  UsersRound,
  Waves,
  X,
} from "lucide-react";
import {
  createBlock,
  createInquiry,
  deleteBlock,
  getOwnerDashboard,
  getPublicBootstrap,
  ownerLogin,
  ownerLogout,
  savePrices,
  updateBooking,
} from "./api";
import { ALL_PHOTOS, EXPERIENCES, FEATURED_PHOTOS, MONTH_NAMES } from "./data";
import type {
  BookingRequest,
  DateRange,
  InquiryPayload,
  MonthPrice,
  OwnerDashboardData,
  PublicBootstrap,
} from "./types";

const money = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

function App() {
  return window.location.pathname.startsWith("/owner") ? <OwnerApp /> : <PublicSite />;
}

function Brand({ light = false }: { light?: boolean }) {
  return (
    <a href="/" className={`brand ${light ? "brand--light" : ""}`} aria-label="Villa Paganini home">
      <span className="brand__eyebrow">Villa</span>
      <span className="brand__name">Paganini</span>
      <span className="brand__place">Javea · Spain</span>
    </a>
  );
}

function PublicSite() {
  const [bootstrap, setBootstrap] = useState<PublicBootstrap | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    void getPublicBootstrap().then(setBootstrap);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToInquiry = () => {
    document.querySelector("#stay")?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div className="site-shell">
      <header className={`public-header ${scrolled ? "public-header--solid" : ""}`}>
        <Brand light={!scrolled} />
        <nav className="desktop-nav" aria-label="Main navigation">
          <a href="#house">The house</a>
          <a href="#gallery">Gallery</a>
          <a href="#javea">Javea</a>
          <a href="#rates">Rates</a>
        </nav>
        <button className="header-cta" onClick={goToInquiry}>Check dates <ArrowRight size={15} /></button>
        <button className="menu-button" onClick={() => setMenuOpen(true)} aria-label="Open menu"><Menu /></button>
      </header>

      {menuOpen && (
        <div className="mobile-menu">
          <button onClick={() => setMenuOpen(false)} aria-label="Close menu"><X /></button>
          <Brand />
          <nav>
            {["house", "gallery", "javea", "rates"].map((item) => (
              <a key={item} href={`#${item}`} onClick={() => setMenuOpen(false)}>{item === "javea" ? "Javea" : `The ${item}`}</a>
            ))}
          </nav>
          <button className="button button--dark" onClick={goToInquiry}>Check dates</button>
        </div>
      )}

      <main>
        <section className="hero">
          <img src="/images/villa/SP0002.jpg" alt="Villa Paganini pool and Mediterranean sea view" className="hero__image" />
          <div className="hero__veil" />
          <div className="hero__content">
            <p className="kicker kicker--light"><span /> A private house in Javea, Costa Blanca</p>
            <h1>A house by the sea.<br /><em>Room to slow down.</em></h1>
            <p className="hero__intro">Four bedrooms, a private pool and a rather good view of the Mediterranean.</p>
          </div>
          <div className="hero__facts">
            <span><strong>13</strong> guests</span>
            <span><strong>4</strong> bedrooms</span>
            <span><strong>2</strong> bathrooms</span>
            <span><Waves size={19} /> private pool</span>
          </div>
          <button className="hero__scroll" onClick={() => document.querySelector("#house")?.scrollIntoView({ behavior: "smooth" })}>
            See the house <ChevronDown size={16} />
          </button>
        </section>

        <section id="house" className="intro-section content-width">
          <div className="intro-section__title">
            <p className="kicker"><span /> Villa Paganini</p>
            <h2>Simple, comfortable<br /><em>and properly relaxing.</em></h2>
          </div>
          <div className="intro-section__copy">
            <p className="lead">Villa Paganini is our family house on the hillside above Javea, with sea views and room for up to 13.</p>
            <p>The main living space opens onto the terrace and pool. There is a well-equipped kitchen, plenty of room to eat inside or out, and good views from most parts of the house.</p>
            <p>It works well for a family or a few friends. The coves, old town and port are all nearby, though the house itself is quiet.</p>
            <a href="#gallery" className="text-link">Take a look around <ArrowRight size={16} /></a>
          </div>
        </section>

        <section id="gallery" className="gallery-feature content-width-wide">
          <button className="gallery-feature__main" onClick={() => setLightbox(0)}>
            <img src={FEATURED_PHOTOS[0].src} alt={FEATURED_PHOTOS[0].alt} />
            <span>Pool & sea</span>
          </button>
          <div className="gallery-feature__side">
            <button onClick={() => setLightbox(3)}><img src={FEATURED_PHOTOS[3].src} alt={FEATURED_PHOTOS[3].alt} /></button>
            <button onClick={() => setLightbox(2)}><img src={FEATURED_PHOTOS[2].src} alt={FEATURED_PHOTOS[2].alt} /></button>
          </div>
          <button className="gallery-count" onClick={() => setLightbox(0)}><Eye size={18} /> View all photos <span>60</span></button>
        </section>

        <section className="details-section content-width">
          <div>
            <p className="kicker"><span /> At the house</p>
            <h2>The useful bits.<br /><em>And a good pool.</em></h2>
          </div>
          <div className="amenity-grid">
            {[
              [Waves, "Private pool", "A private pool, terrace, loungers and views towards the sea."],
              [Sparkles, "Living space", "The main living area opens directly onto the terrace."],
              [UsersRound, "Sleeps up to 13", "Four bedrooms, including a flexible spillover room with a sofa bed."],
              [Clock3, "Well equipped", "Air conditioning, Wi-Fi, a full kitchen, washing machine and family essentials."],
            ].map(([Icon, title, copy]) => {
              const AmenityIcon = Icon as typeof Waves;
              return <article key={String(title)}><AmenityIcon /><h3>{String(title)}</h3><p>{String(copy)}</p></article>;
            })}
          </div>
        </section>

        <section className="quote-band">
          <img src="/images/villa/SP0030.jpg" alt="Dining at Villa Paganini with Mediterranean views" />
          <blockquote>A proper holiday house: comfortable, unfussy and close to the sea.</blockquote>
        </section>

        <section id="javea" className="javea-section content-width-wide">
          <div className="javea-heading">
            <p className="kicker"><span /> Around Javea</p>
            <h2>Beach, town<br /><em>and a decent lunch.</em></h2>
            <p>Javea is split between the old town, the port and the Arenal beach. There are good coves, useful walks and plenty of places to eat.</p>
          </div>
          <div className="experience-list">
            {EXPERIENCES.map((experience) => (
              <article key={experience.number}>
                <span>{experience.number}</span>
                <div><small>{experience.meta}</small><h3>{experience.title}</h3><p>{experience.text}</p></div>
                <ArrowRight />
              </article>
            ))}
          </div>
        </section>

        <section className="photo-strip" aria-label="More photographs of Villa Paganini">
          {FEATURED_PHOTOS.slice(4, 8).map((photo, index) => (
            <button key={photo.src} onClick={() => setLightbox(index + 4)}><img src={photo.src} alt={photo.alt} loading="lazy" /></button>
          ))}
        </section>

        <RatesSection prices={bootstrap?.prices || []} />

        <InquirySection bootstrap={bootstrap} />
      </main>

      <footer>
        <div className="footer__top content-width-wide">
          <Brand />
          <div><p>Thinking of coming?</p><button onClick={goToInquiry}>Check the dates <ArrowRight /></button></div>
        </div>
        <div className="footer__bottom content-width-wide">
          <span>© {new Date().getFullYear()} Villa Paganini</span>
          <span>Javea · Alicante · Spain</span>
          <a href="/owner"><KeyRound size={13} /> Owner area</a>
        </div>
      </footer>

      {lightbox !== null && <Lightbox startIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function RatesSection({ prices }: { prices: MonthPrice[] }) {
  const current = prices.length ? prices : [];
  return (
    <section id="rates" className="rates-section content-width">
      <div className="rates-intro">
        <p className="kicker"><span /> Rates</p>
        <h2>Prices by month.<br /><em>No booking fees.</em></h2>
        <p>Send us the dates you have in mind. We will check the calendar and confirm the price by email. The site does not take payment.</p>
      </div>
      <div className="rate-list">
        {current.map((price) => (
          <div key={price.month}>
            <span>{MONTH_NAMES[price.month - 1]}</span>
            <span>{price.minimumNights} night minimum</span>
            <strong>{money.format(price.nightlyRate)} <small>/ night</small></strong>
          </div>
        ))}
        {!current.length && <div className="rates-loading">Loading seasonal rates…</div>}
      </div>
    </section>
  );
}

function InquirySection({ bootstrap }: { bootstrap: PublicBootstrap | null }) {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [form, setForm] = useState({ guestName: "", guestEmail: "", phone: "", message: "", website: "" });
  const [state, setState] = useState<{ kind: "idle" | "sending" | "sent" | "error"; message?: string }>({ kind: "idle" });

  const unavailable = bootstrap?.unavailable || [];
  const nights = startDate && endDate ? differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) : 0;
  const estimate = useMemo(() => {
    if (!startDate || !endDate || !bootstrap) return 0;
    return eachDayOfInterval({ start: parseISO(startDate), end: addDays(parseISO(endDate), -1) })
      .reduce((total, day) => total + (bootstrap.prices.find((p) => p.month === day.getMonth() + 1)?.nightlyRate || 0), 0);
  }, [bootstrap, endDate, startDate]);

  const isUnavailable = (date: Date) => unavailable.some((range) => {
    const start = parseISO(range.startDate);
    const end = parseISO(range.endDate);
    return !isBefore(date, start) && isBefore(date, end);
  });

  const selectDate = (date: Date) => {
    if (isUnavailable(date) || isBefore(date, startOfDaySafe(new Date()))) return;
    const value = format(date, "yyyy-MM-dd");
    if (!startDate || endDate || isBefore(date, parseISO(startDate))) {
      setStartDate(value);
      setEndDate("");
      return;
    }
    const rangeDays = eachDayOfInterval({ start: parseISO(startDate), end: date });
    if (rangeDays.some(isUnavailable)) {
      setStartDate(value);
      setEndDate("");
      setState({ kind: "error", message: "Those dates cross an unavailable stay, so we started a new selection." });
      return;
    }
    setEndDate(value);
    setState({ kind: "idle" });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      setState({ kind: "error", message: "Choose your arrival and departure dates first." });
      return;
    }
    setState({ kind: "sending" });
    const payload: InquiryPayload = { ...form, adults, children, startDate, endDate };
    try {
      await createInquiry(payload);
      setState({ kind: "sent" });
    } catch (error) {
      setState({ kind: "error", message: error instanceof Error ? error.message : "Please try again." });
    }
  };

  return (
    <section id="stay" className="inquiry-section">
      <div className="content-width-wide inquiry-section__heading">
        <p className="kicker kicker--light"><span /> Enquire</p>
        <h2>See if your dates<br /><em>are free.</em></h2>
        <p>Choose your dates and send us a few details. We normally reply within a day.</p>
      </div>
      <div className="inquiry-card content-width-wide">
        {state.kind === "sent" ? (
          <div className="success-panel">
            <span><Check /></span>
            <p className="kicker"><span /> Request received</p>
            <h3>Thanks. We’ll have a look.</h3>
            <p>Your dates are not booked just yet. Gilbert and Louise will check the calendar and get back to you by email.</p>
            <button className="button button--dark" onClick={() => setState({ kind: "idle" })}>Send another request</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="calendar-pane">
              <div className="calendar-pane__nav">
                <div><small>Choose dates</small><h3>{startDate ? format(parseISO(startDate), "d MMM") : "Arrival"} <ArrowRight size={18} /> {endDate ? format(parseISO(endDate), "d MMM") : "Departure"}</h3></div>
                <div><button type="button" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month"><ChevronLeft /></button><button type="button" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month"><ChevronRight /></button></div>
              </div>
              <div className="calendar-pair">
                <CalendarMonth month={month} {...{ startDate, endDate, selectDate, isUnavailable }} />
                <CalendarMonth month={addMonths(month, 1)} {...{ startDate, endDate, selectDate, isUnavailable }} />
              </div>
              <div className="calendar-key"><span><i /> Available</span><span><i className="booked" /> Unavailable</span><span><i className="selected" /> Your dates</span></div>
            </div>
            <div className="form-pane">
              <div className="guest-row">
                <GuestCounter label="Adults" hint="Age 16+" value={adults} setValue={setAdults} min={1} max={13 - children} />
                <GuestCounter label="Children" hint="Under 16" value={children} setValue={setChildren} min={0} max={13 - adults} />
              </div>
              <label>Your name<input required autoComplete="name" value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} placeholder="Jane Smith" /></label>
              <label>Email address<input required type="email" autoComplete="email" value={form.guestEmail} onChange={(e) => setForm({ ...form, guestEmail: e.target.value })} placeholder="jane@example.com" /></label>
              <label>Phone <small>optional</small><input type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+44 7700 000000" /></label>
              <label>Anything useful to know? <small>optional</small><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Children, a special occasion, or anything else worth mentioning…" /></label>
              <input className="honeypot" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} aria-hidden="true" />
              {state.kind === "error" && <p className="form-error">{state.message}</p>}
              {nights > 0 && <div className="estimate"><div><span>{nights} nights</span><small>Estimated stay</small></div><strong>{money.format(estimate)}</strong></div>}
              <button className="button button--coral button--full" disabled={state.kind === "sending"}>{state.kind === "sending" ? "Sending…" : "Send stay request"}<ArrowRight size={17} /></button>
              <p className="form-note"><ShieldCheck size={14} /> This is a request, not an instant booking. No payment is taken.</p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function CalendarMonth({
  month,
  startDate,
  endDate,
  selectDate,
  isUnavailable,
}: {
  month: Date;
  startDate: string;
  endDate: string;
  selectDate: (date: Date) => void;
  isUnavailable: (date: Date) => boolean;
}) {
  const first = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const last = addDays(first, 41);
  const days = eachDayOfInterval({ start: first, end: last });
  const selectedStart = startDate ? parseISO(startDate) : null;
  const selectedEnd = endDate ? parseISO(endDate) : null;

  return (
    <div className="calendar-month">
      <h4>{format(month, "MMMM yyyy")}</h4>
      <div className="calendar-weekdays">{["M", "T", "W", "T", "F", "S", "S"].map((day, i) => <span key={`${day}-${i}`}>{day}</span>)}</div>
      <div className="calendar-days">
        {days.map((day) => {
          const disabled = isUnavailable(day) || isBefore(day, startOfDaySafe(new Date())) || !isSameMonth(day, month);
          const start = selectedStart && isSameDay(day, selectedStart);
          const end = selectedEnd && isSameDay(day, selectedEnd);
          const between = selectedStart && selectedEnd && isAfter(day, selectedStart) && isBefore(day, selectedEnd);
          return (
            <button type="button" key={day.toISOString()} disabled={disabled} onClick={() => selectDate(day)} className={`${start || end ? "is-edge" : ""} ${between ? "is-between" : ""}`}>
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GuestCounter({ label, hint, value, setValue, min, max }: { label: string; hint: string; value: number; setValue: (value: number) => void; min: number; max: number }) {
  return <div className="guest-counter"><div><strong>{label}</strong><small>{hint}</small></div><div><button type="button" onClick={() => setValue(Math.max(min, value - 1))} aria-label={`Fewer ${label}`}><Minus /></button><span>{value}</span><button type="button" onClick={() => setValue(Math.min(max, value + 1))} aria-label={`More ${label}`}><Plus /></button></div></div>;
}

function Lightbox({ startIndex, onClose }: { startIndex: number; onClose: () => void }) {
  const [index, setIndex] = useState(Math.min(startIndex, ALL_PHOTOS.length - 1));
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") setIndex((value) => (value + 1) % ALL_PHOTOS.length);
      if (event.key === "ArrowLeft") setIndex((value) => (value - 1 + ALL_PHOTOS.length) % ALL_PHOTOS.length);
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="Villa photo gallery">
      <button className="lightbox__close" onClick={onClose} aria-label="Close gallery"><X /></button>
      <button className="lightbox__prev" onClick={() => setIndex((index - 1 + ALL_PHOTOS.length) % ALL_PHOTOS.length)} aria-label="Previous photo"><ArrowLeft /></button>
      <img src={ALL_PHOTOS[index].src} alt={ALL_PHOTOS[index].alt} />
      <button className="lightbox__next" onClick={() => setIndex((index + 1) % ALL_PHOTOS.length)} aria-label="Next photo"><ArrowRight /></button>
      <span>{String(index + 1).padStart(2, "0")} / {ALL_PHOTOS.length}</span>
    </div>
  );
}

function OwnerApp() {
  const [authState, setAuthState] = useState<"checking" | "login" | "authenticated">("checking");
  const [dashboard, setDashboard] = useState<OwnerDashboardData | null>(null);
  const [tab, setTab] = useState<"requests" | "calendar" | "rates">("requests");
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const data = await getOwnerDashboard();
      setDashboard(data);
      setAuthState("authenticated");
      setError("");
    } catch {
      setAuthState("login");
    }
  };

  useEffect(() => { void refresh(); }, []);

  if (authState === "checking") return <div className="owner-loading"><Brand /><span /></div>;
  if (authState === "login") return <OwnerLogin onSuccess={refresh} />;

  const logout = async () => {
    await ownerLogout();
    setDashboard(null);
    setAuthState("login");
  };

  return (
    <div className="owner-shell">
      <aside className="owner-sidebar">
        <Brand />
        <div className="owner-sidebar__label">Owner dashboard</div>
        <nav>
          <button className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")}><CircleUserRound /> Requests {dashboard && dashboard.stats.pending > 0 && <span>{dashboard.stats.pending}</span>}</button>
          <button className={tab === "calendar" ? "active" : ""} onClick={() => setTab("calendar")}><CalendarDays /> Availability</button>
          <button className={tab === "rates" ? "active" : ""} onClick={() => setTab("rates")}><Euro /> Seasonal rates</button>
        </nav>
        <div className="owner-sidebar__bottom">
          <a href="/" target="_blank"><Eye /> View live site</a>
          <button onClick={logout}><LogOut /> Log out</button>
        </div>
      </aside>
      <main className="owner-main">
        <div className="owner-mobile-head"><Brand /><button onClick={logout}><LogOut /></button></div>
        <nav className="owner-mobile-tabs" aria-label="Owner sections">
          <button className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")}><CircleUserRound /> Requests</button>
          <button className={tab === "calendar" ? "active" : ""} onClick={() => setTab("calendar")}><CalendarDays /> Dates</button>
          <button className={tab === "rates" ? "active" : ""} onClick={() => setTab("rates")}><Euro /> Rates</button>
        </nav>
        {error && <div className="owner-error">{error}<button onClick={() => setError("")}><X /></button></div>}
        {dashboard && tab === "requests" && <RequestsPanel data={dashboard} refresh={refresh} setError={setError} />}
        {dashboard && tab === "calendar" && <BlocksPanel data={dashboard} refresh={refresh} setError={setError} />}
        {dashboard && tab === "rates" && <PricesPanel prices={dashboard.prices} refresh={refresh} setError={setError} />}
      </main>
    </div>
  );
}

function OwnerLogin({ onSuccess }: { onSuccess: () => Promise<void> }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setLoading(true); setError("");
    try { await ownerLogin(password); await onSuccess(); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not sign in."); }
    finally { setLoading(false); }
  };
  return (
    <div className="owner-login">
      <div className="owner-login__photo"><img src="/images/villa/SP0001.jpg" alt="Villa Paganini" /><div><p>Villa Paganini</p><span>Private owner area</span></div></div>
      <div className="owner-login__form">
        <a href="/" className="back-link"><ArrowLeft /> Back to the villa</a>
        <Brand />
        <form onSubmit={submit}>
          <span className="login-icon"><KeyRound /></span>
          <p className="kicker"><span /> Owner access</p>
          <h1>Welcome back.</h1>
          <p>Sign in to manage requests, dates and seasonal pricing.</p>
          <label>Password<input autoFocus required type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter owner password" /></label>
          {error && <p className="form-error">{error}</p>}
          <button className="button button--dark button--full" disabled={loading}>{loading ? "Signing in…" : "Open dashboard"}<ArrowRight size={16} /></button>
        </form>
      </div>
    </div>
  );
}

function OwnerPageHeader({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return <header className="owner-page-head"><div><p>{eyebrow}</p><h1>{title}</h1><span>{description}</span></div><div className="owner-avatar">G + L</div></header>;
}

function RequestsPanel({ data, refresh, setError }: { data: OwnerDashboardData; refresh: () => Promise<void>; setError: (value: string) => void }) {
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [selected, setSelected] = useState<BookingRequest | null>(null);
  const bookings = data.bookings.filter((booking) => filter === "all" || booking.status === filter);
  const act = async (booking: BookingRequest, status: "approved" | "rejected") => {
    try { await updateBooking(booking.id, status); setSelected(null); await refresh(); }
    catch (error) { setError(error instanceof Error ? error.message : "Could not update request."); }
  };
  return (
    <>
      <OwnerPageHeader eyebrow="Bookings" title="Stay requests" description="Review each request personally, then approve or decline it." />
      <div className="stats-grid">
        <article><span>Awaiting reply</span><strong>{data.stats.pending}</strong><small>requests</small></article>
        <article><span>Confirmed stays</span><strong>{data.stats.approved}</strong><small>bookings</small></article>
        <article><span>Nights booked</span><strong>{data.stats.bookedNights}</strong><small>this calendar</small></article>
        <article><span>Confirmed value</span><strong>{money.format(data.stats.projectedRevenue)}</strong><small>estimated</small></article>
      </div>
      <section className="owner-panel">
        <div className="owner-panel__toolbar"><div>{(["all", "pending", "approved", "rejected"] as const).map((item) => <button key={item} className={filter === item ? "active" : ""} onClick={() => setFilter(item)}>{item}</button>)}</div><span>{bookings.length} requests</span></div>
        <div className="booking-table">
          {bookings.map((booking) => (
            <button className="booking-row" key={booking.id} onClick={() => setSelected(booking)}>
              <span className={`status-dot status-dot--${booking.status}`} />
              <div className="booking-guest"><strong>{booking.guestName}</strong><small>{booking.guestEmail}</small></div>
              <div><strong>{format(parseISO(booking.startDate), "d MMM")} — {format(parseISO(booking.endDate), "d MMM yyyy")}</strong><small>{differenceInCalendarDays(parseISO(booking.endDate), parseISO(booking.startDate))} nights · {booking.adults + booking.children} guests</small></div>
              <span className={`status-pill status-pill--${booking.status}`}>{booking.status}</span>
              <strong>{money.format(booking.quotedPrice)}</strong>
              <ChevronRight />
            </button>
          ))}
          {!bookings.length && <div className="empty-state"><CalendarDays /><h3>No requests here</h3><p>New stay requests will appear in this list.</p></div>}
        </div>
      </section>
      {selected && <RequestDrawer booking={selected} close={() => setSelected(null)} act={act} />}
    </>
  );
}

function RequestDrawer({ booking, close, act }: { booking: BookingRequest; close: () => void; act: (booking: BookingRequest, status: "approved" | "rejected") => Promise<void> }) {
  const nights = differenceInCalendarDays(parseISO(booking.endDate), parseISO(booking.startDate));
  return <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && close()}><aside className="request-drawer"><button className="drawer-close" onClick={close}><X /></button><p className="kicker"><span /> Stay request</p><h2>{booking.guestName}</h2><span className={`status-pill status-pill--${booking.status}`}>{booking.status}</span><div className="drawer-dates"><CalendarDays /><div><small>Requested dates</small><strong>{format(parseISO(booking.startDate), "EEEE d MMMM")} —<br />{format(parseISO(booking.endDate), "EEEE d MMMM yyyy")}</strong><span>{nights} nights</span></div></div><dl><div><dt>Guests</dt><dd>{booking.adults} adults{booking.children ? `, ${booking.children} children` : ""}</dd></div><div><dt>Email</dt><dd><a href={`mailto:${booking.guestEmail}`}>{booking.guestEmail}</a></dd></div>{booking.phone && <div><dt>Phone</dt><dd>{booking.phone}</dd></div>}<div><dt>Estimated stay</dt><dd>{money.format(booking.quotedPrice)}</dd></div><div><dt>Received</dt><dd>{format(parseISO(booking.createdAt), "d MMM yyyy, HH:mm")}</dd></div></dl>{booking.message && <div className="guest-message"><small>Guest message</small><p>“{booking.message}”</p></div>}{booking.status === "pending" && <div className="drawer-actions"><button className="button button--approve" onClick={() => void act(booking, "approved")}><Check /> Approve dates</button><button className="button button--reject" onClick={() => void act(booking, "rejected")}><X /> Decline</button><p>Both owners will be emailed when you decide, ready for a personal reply to the guest.</p></div>}</aside></div>;
}

function BlocksPanel({ data, refresh, setError }: { data: OwnerDashboardData; refresh: () => Promise<void>; setError: (value: string) => void }) {
  const [form, setForm] = useState({ startDate: "", endDate: "", label: "Owner stay" });
  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    try { await createBlock(form); setForm({ startDate: "", endDate: "", label: "Owner stay" }); await refresh(); }
    catch (error) { setError(error instanceof Error ? error.message : "Could not block dates."); }
  };
  const remove = async (id: string) => {
    try { await deleteBlock(id); await refresh(); }
    catch (error) { setError(error instanceof Error ? error.message : "Could not remove block."); }
  };
  const allRanges = [...data.blocks, ...data.bookings.filter((b) => b.status === "approved").map((b): DateRange => ({ id: b.id, startDate: b.startDate, endDate: b.endDate, kind: "approved", label: b.guestName }))].sort((a, b) => a.startDate.localeCompare(b.startDate));
  return <><OwnerPageHeader eyebrow="Calendar" title="Availability" description="Block owner stays and see every confirmed booking at a glance." /><div className="availability-layout"><section className="owner-panel block-form"><h2>Block dates</h2><p>Use this for family stays, maintenance or any dates that should not be requested.</p><form onSubmit={add}><label>From<input required type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label><label>Until<input required type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label><label>Reason<input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></label><button className="button button--dark button--full"><Plus /> Add blocked dates</button></form></section><section className="owner-panel upcoming-ranges"><div><h2>Upcoming dates</h2><span><i className="confirmed" /> Guest stay <i className="blocked" /> Owner block</span></div>{allRanges.length ? allRanges.map((range) => <article key={`${range.kind}-${range.id}`}><div className={`range-date range-date--${range.kind}`}><strong>{format(parseISO(range.startDate), "dd")}</strong><small>{format(parseISO(range.startDate), "MMM")}</small></div><div><strong>{range.label || (range.kind === "blocked" ? "Blocked" : "Confirmed guest")}</strong><small>{format(parseISO(range.startDate), "d MMM")} — {format(parseISO(range.endDate), "d MMM yyyy")} · {differenceInCalendarDays(parseISO(range.endDate), parseISO(range.startDate))} nights</small></div><span className={`status-pill status-pill--${range.kind === "approved" ? "approved" : "blocked"}`}>{range.kind === "approved" ? "Guest" : "Blocked"}</span>{range.kind === "blocked" && <button onClick={() => void remove(range.id)} aria-label="Remove blocked dates"><Trash2 /></button>}</article>) : <div className="empty-state"><CalendarDays /><h3>The calendar is wide open</h3><p>Add a block or approve a guest request to see it here.</p></div>}</section></div></>;
}

function PricesPanel({ prices, refresh, setError }: { prices: MonthPrice[]; refresh: () => Promise<void>; setError: (value: string) => void }) {
  const [draft, setDraft] = useState(prices);
  const [saving, setSaving] = useState(false);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setSaving(true);
    try { await savePrices(draft); await refresh(); }
    catch (error) { setError(error instanceof Error ? error.message : "Could not save rates."); }
    finally { setSaving(false); }
  };
  return <><OwnerPageHeader eyebrow="Pricing" title="Seasonal rates" description="Set one nightly price and minimum stay for each month." /><section className="owner-panel price-panel"><div className="price-panel__intro"><div><h2>Rates by month</h2><p>These are shown publicly and used to estimate every request. You will still confirm the final price personally.</p></div><span><Euro /> EUR</span></div><form onSubmit={submit}><div className="price-grid">{draft.map((price, index) => <label key={price.month}><span>{MONTH_NAMES[price.month - 1]}</span><div><small>€</small><input type="number" min="0" step="5" value={price.nightlyRate} onChange={(e) => setDraft(draft.map((item, i) => i === index ? { ...item, nightlyRate: Number(e.target.value) } : item))} /><small>/ night</small></div><select value={price.minimumNights} onChange={(e) => setDraft(draft.map((item, i) => i === index ? { ...item, minimumNights: Number(e.target.value) } : item))}>{[2,3,4,5,6,7,10,14].map((n) => <option key={n} value={n}>{n} night minimum</option>)}</select></label>)}</div><div className="price-save"><p><ShieldCheck /> Changes appear on the public site immediately after saving.</p><button className="button button--dark" disabled={saving}>{saving ? "Saving…" : "Save all rates"}<Check /></button></div></form></section></>;
}

function startOfDaySafe(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export default App;
